import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Navigation,
  RefreshCw,
  ArrowLeft,
  Truck,
  User,
  Clock,
  Activity,
} from 'lucide-react';
import { formatRelativeTime, formatDate } from '@/lib/utils';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import api from '@/lib/axios';
import { PageHeader } from '@/components/common/PageHeader';
import { BookingStatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useBookings } from '../hooks/useBookings';

interface TrackingInfo {
  bookingId: string;
  status: string;
  driver?: {
    id: string;
    name: string;
    phone: string;
    vehicleNumber: string;
    currentLocation?: { lat: number; lng: number };
    isOnline: boolean;
    lastSeen?: string;
  };
  customer?: {
    id: string;
    name: string;
    phone: string;
  };
  pickup: { address: string; lat: number; lng: number };
  dropoff: { address: string; lat: number; lng: number };
  estimatedArrival?: string;
  distanceCovered?: number;
  totalDistance?: number;
}

function useBookingTracking(bookingId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['tracking', bookingId],
    queryFn: async (): Promise<TrackingInfo> => {
      const response = await api.get(API_ENDPOINTS.BOOKINGS.TRACKING(bookingId!));
      const d = response.data?.data || response.data;
      return {
        bookingId: d.bookingId || bookingId!,
        status: d.status,
        driver: d.driver,
        customer: d.customer,
        pickup: d.pickup || { address: 'N/A', lat: 0, lng: 0 },
        dropoff: d.dropoff || { address: 'N/A', lat: 0, lng: 0 },
        estimatedArrival: d.estimatedArrival,
        distanceCovered: d.distanceCovered,
        totalDistance: d.totalDistance,
      };
    },
    enabled: !!bookingId && enabled,
    refetchInterval: 10000, // Refresh every 10 seconds
    staleTime: 5000,
  });
}

export function LiveTrackingPage() {
  const navigate = useNavigate();
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Get active bookings (ongoing)
  const { data: bookingsData } = useBookings({ page: 1, limit: 50, status: 'ongoing' });
  const activeBookings = bookingsData?.data || [];

  const { data: tracking, isLoading: trackingLoading, refetch } = useBookingTracking(
    selectedBookingId,
    autoRefresh,
  );

  const bookingOptions = activeBookings.map((b) => ({
    value: b.id,
    label: `${b.bookingNumber || b.id.slice(0, 8)} — ${b.customer?.name || 'Unknown'}`,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Tracking"
        description="Monitor active delivery trips in real-time"
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      {/* Controls */}
      <Card>
        <CardContent className="flex items-center gap-4 p-4">
          <div className="flex-1">
            <Select
              value={selectedBookingId || ''}
              onChange={(value) => setSelectedBookingId(value || null)}
              options={[
                { value: '', label: 'Select an active booking to track...' },
                ...bookingOptions,
              ]}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={!selectedBookingId}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            disabled={!selectedBookingId}
          >
            <Activity className="h-4 w-4 mr-2" />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
        </CardContent>
      </Card>

      {/* Active bookings summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-full bg-blue-100 p-2">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active Trips</p>
              <p className="text-2xl font-bold">{activeBookings.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-full bg-green-100 p-2">
              <Navigation className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Drivers Online</p>
              <p className="text-2xl font-bold">{activeBookings.filter((b) => b.driver).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-full bg-amber-100 p-2">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Updated</p>
              <p className="text-sm font-medium">{new Date().toLocaleTimeString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tracking Details */}
      {selectedBookingId && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Tracking Info Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Trip Details</span>
                {tracking && <BookingStatusBadge status={tracking.status as any} />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trackingLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : tracking ? (
                <div className="space-y-4">
                  {/* Driver Info */}
                  {tracking.driver && (
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="rounded-full bg-green-100 p-2 mt-0.5">
                        <Truck className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{tracking.driver.name}</p>
                        <p className="text-xs text-muted-foreground">{tracking.driver.phone}</p>
                        <p className="text-xs text-muted-foreground">{tracking.driver.vehicleNumber}</p>
                        {tracking.driver.currentLocation && (
                          <p className="text-xs text-green-600 mt-1">
                            📍 {tracking.driver.currentLocation.lat.toFixed(4)}, {tracking.driver.currentLocation.lng.toFixed(4)}
                          </p>
                        )}
                      </div>
                      <Badge variant={tracking.driver.isOnline ? 'default' : 'secondary'} className="text-xs">
                        {tracking.driver.isOnline ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                  )}

                  {/* Customer Info */}
                  {tracking.customer && (
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="rounded-full bg-blue-100 p-2 mt-0.5">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tracking.customer.name}</p>
                        <p className="text-xs text-muted-foreground">{tracking.customer.phone}</p>
                      </div>
                    </div>
                  )}

                  {/* Route */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="mt-1 h-3 w-3 rounded-full bg-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Pickup</p>
                        <p className="text-sm">{tracking.pickup.address}</p>
                      </div>
                    </div>
                    <div className="ml-1.5 w-0.5 h-4 bg-border" />
                    <div className="flex items-start gap-2">
                      <div className="mt-1 h-3 w-3 rounded-full bg-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Dropoff</p>
                        <p className="text-sm">{tracking.dropoff.address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  {tracking.totalDistance && (
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Distance covered</span>
                        <span>
                          {(tracking.distanceCovered || 0).toFixed(1)} / {tracking.totalDistance.toFixed(1)} km
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, ((tracking.distanceCovered || 0) / tracking.totalDistance) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {tracking.estimatedArrival && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">ETA:</span>
                      <span className="font-medium">{formatRelativeTime(tracking.estimatedArrival)}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No tracking data available for this booking.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Map placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Map View</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg overflow-hidden bg-muted/30 border border-dashed flex flex-col items-center justify-center" style={{ height: 380 }}>
                {tracking?.driver?.currentLocation ? (
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <Navigation className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Driver Location</p>
                      <p className="text-sm text-muted-foreground">
                        Lat: {tracking.driver.currentLocation.lat.toFixed(6)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Lng: {tracking.driver.currentLocation.lng.toFixed(6)}
                      </p>
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${tracking.driver.currentLocation.lat},${tracking.driver.currentLocation.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" size="sm">
                        <MapPin className="h-4 w-4 mr-2" />
                        Open in Google Maps
                      </Button>
                    </a>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Live map unavailable</p>
                    <p className="text-sm">Driver location will appear here when available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active bookings list */}
      {!selectedBookingId && activeBookings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Trips</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedBookingId(booking.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Truck className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{booking.bookingNumber || booking.id.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">{booking.customer?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{booking.driver?.name || 'No driver'}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(booking.createdAt)}</p>
                    </div>
                    <BookingStatusBadge status={booking.status as any} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedBookingId && activeBookings.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Truck className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No active trips</p>
            <p className="text-sm text-muted-foreground mt-1">
              Active bookings will appear here for tracking.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
